/**
 * Redline Reaper Society product page.
 *
 * Sits on top of Horizon's <variant-picker> and <product-form-component> (they still handle
 * variant changes, price, add to cart, analytics and quick add) and adds:
 * - Pick a size first: no size is pre-selected and add to cart reads "Select a size" until one is
 *   picked, so nobody gets the wrong size by accident.
 * - Instant photo swap on color change, swipe gallery with thumbnails, full-screen viewer.
 * - Size guide drawer with inches and centimeters.
 * - Sticky buy bar, delivery dates, free-shipping progress, opening the cart after adding.
 * - "Make it three" bundle add to cart and the review form opener.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';

const EVENTS = {
  productSelect: 'shopify:product:select',
  cartLinesUpdate: 'shopify:cart:lines-update',
  cartDiscountUpdate: 'shopify:cart:discount-update',
  selection: 'rr:selection',
  needSize: 'rr:need-size',
  cartTotal: 'rr:cart-total',
};

const EIGHTHS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];

/* ---------- helpers ---------- */

/**
 * @param {'localStorage' | 'sessionStorage'} area
 * @param {string} key
 */
function storageGet(area, key) {
  try {
    return window[area].getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {'localStorage' | 'sessionStorage'} area
 * @param {string} key
 * @param {string | null} value - null removes the key
 */
function storageSet(area, key, value) {
  try {
    if (value === null) window[area].removeItem(key);
    else window[area].setItem(key, value);
  } catch {
    /* storage can be blocked; the page works without it */
  }
}

/** @param {Element | null} el */
function parseJSON(el) {
  try {
    return JSON.parse(el?.textContent || 'null');
  } catch {
    return null;
  }
}

/**
 * Replaces [placeholders] in merchant text.
 * @param {string | undefined} template
 * @param {Record<string, string | number>} values
 */
function fill(template, values) {
  return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
}

/** @param {string | undefined} list - "Black|White|Navy" */
function splitList(list) {
  return String(list || '')
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * "Black, White and Navy"
 * @param {string[]} items
 * @param {string} [and]
 */
function joinList(items, and = 'and') {
  if (items.length < 2) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

/**
 * Formats a garment measurement stored in inches: eighths as fractions, or whole centimeters.
 * Matches snippets/rrs-measure.liquid.
 * @param {string | undefined} raw
 * @param {string} unit - 'in' or 'cm'
 * @param {boolean} [withUnit]
 */
function formatMeasure(raw, unit, withUnit = false) {
  const value = parseFloat(String(raw ?? '').replace(',', '.'));
  if (!Number.isFinite(value)) return String(raw ?? '');
  let out;
  if (unit === 'cm') {
    out = String(Math.round(value * 2.54));
  } else {
    const eighths = value * 8;
    if (Math.abs(eighths - Math.round(eighths)) < 0.01) {
      const rounded = Math.round(eighths);
      const whole = Math.floor(rounded / 8);
      const part = rounded % 8;
      out = part ? `${whole || ''}${EIGHTHS[part]}` : String(whole);
    } else {
      out = String(raw).trim();
    }
  }
  return withUnit ? `${out} ${unit}` : out;
}

/** Money settings from the product section, the bundle or the store defaults. */
function moneySettings() {
  const host = /** @type {HTMLElement | null} */ (document.querySelector('rr-product'));
  const config = parseJSON(document.querySelector('[data-rr-bun-config]')) || {};
  return {
    format: host?.dataset.moneyFormat || config.moneyFormat || '${{amount}}',
    currency: host?.dataset.currency || config.currency || 'USD',
  };
}

/**
 * Formats cents like the money filter. With trimZeros, whole amounts drop ".00"
 * (like money_without_trailing_zeros).
 * @param {number} cents
 * @param {boolean} [trimZeros]
 */
function money(cents, trimZeros = false) {
  const { format, currency } = moneySettings();
  let template = format;
  if (trimZeros && Math.round(cents) % 100 === 0) {
    template = template
      .replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}')
      .replace(/{{\s*amount_with_comma_separator\s*}}/, '{{amount_no_decimals_with_comma_separator}}')
      .replace(/{{\s*amount_with_space_separator\s*}}/, '{{amount_no_decimals_with_space_separator}}');
  }
  try {
    return formatMoney(Math.round(cents), template, currency);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function cartUrl() {
  return window.Theme?.routes?.cart_url || '/cart';
}

function cartAddUrl() {
  return window.Theme?.routes?.cart_add_url || '/cart/add.js';
}

async function fetchCart() {
  const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

function smoothBehavior() {
  return prefersReducedMotion() ? 'auto' : 'smooth';
}

/** Opens the cart drawer unless the theme already does it on add. */
function openCartDrawer() {
  if (document.querySelector('cart-drawer-component[auto-open]')) return;
  const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
}

/* ---------- delivery dates ---------- */

/**
 * @param {Date} date
 * @param {number} days
 */
function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * @param {Date} date
 * @param {number} days - business days (Monday to Friday)
 */
function addBusinessDays(date, days) {
  const next = new Date(date);
  let left = days;
  while (left > 0) {
    next.setDate(next.getDate() + 1);
    const day = next.getDay();
    if (day !== 0 && day !== 6) left -= 1;
  }
  return next;
}

/** @param {HTMLElement} el */
function deliveryWindow(el) {
  const number = (/** @type {string | undefined} */ value, /** @type {number} */ fallback) => {
    const parsed = parseInt(value || '', 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  };
  const makeMin = number(el.dataset.makeMin, 7);
  const makeMax = Math.max(makeMin, number(el.dataset.makeMax, 14));
  const transitMin = number(el.dataset.transitMin, 2);
  const transitMax = Math.max(transitMin, number(el.dataset.transitMax, 5));
  const today = new Date();
  return {
    start: addBusinessDays(addDays(today, makeMin), transitMin),
    end: addBusinessDays(addDays(today, makeMax), transitMax),
  };
}

function renderDeliveryDates() {
  const locale = document.documentElement.lang || navigator.language || 'en-US';
  /** @param {Date} date @param {Intl.DateTimeFormatOptions} options */
  const format = (date, options) => {
    try {
      return date.toLocaleDateString(locale, options);
    } catch {
      return date.toLocaleDateString('en-US', options);
    }
  };
  const long = { weekday: 'short', month: 'short', day: 'numeric' };
  const short = { month: 'short', day: 'numeric' };

  const rows = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('[data-rr-eta]'));
  for (const el of rows) {
    const { start, end } = deliveryWindow(el);
    el.textContent = fill(el.dataset.template || '[start] – [end]', {
      start: format(start, /** @type {Intl.DateTimeFormatOptions} */ (long)),
      end: format(end, /** @type {Intl.DateTimeFormatOptions} */ (long)),
    });
  }

  const source = rows[0];
  if (!source) return;
  const { start, end } = deliveryWindow(source);
  const messages = parseJSON(document.querySelector('[data-rr-messages]')) || {};
  const and = messages.and || 'and';
  for (const el of document.querySelectorAll('[data-rr-eta-range]')) {
    el.textContent = `${format(start, /** @type {Intl.DateTimeFormatOptions} */ (short))} ${and} ${format(
      end,
      /** @type {Intl.DateTimeFormatOptions} */ (short)
    )}`;
  }
}

/* ---------- free-shipping progress ---------- */

const freeShipping = {
  /** @type {number | null} */
  total: null,

  /** @param {number} total - cart total in cents */
  render(total) {
    this.total = total;
    const rows = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('[data-rr-fs]'));
    for (const el of rows) {
      const threshold = Number(el.dataset.threshold) || 0;
      if (!threshold) continue;
      const state = total >= threshold ? 'done' : total > 0 ? 'progress' : 'empty';
      const text = el.querySelector('[data-rr-fs-text]');
      if (text) {
        text.textContent = fill(el.dataset[state], {
          threshold: el.dataset.thresholdLabel || money(threshold, true),
          remaining: money(Math.max(threshold - total, 0), true),
        });
      }
      el.classList.remove('rr-fs--empty', 'rr-fs--progress', 'rr-fs--done');
      el.classList.add(`rr-fs--${state}`);
      const bar = /** @type {HTMLElement | null} */ (el.querySelector('[data-rr-fs-bar]'));
      if (bar) {
        bar.hidden = total <= 0;
        const fillEl = /** @type {HTMLElement | null} */ (bar.querySelector('i'));
        if (fillEl) fillEl.style.width = `${Math.min(100, (total / threshold) * 100)}%`;
      }
      const link = /** @type {HTMLAnchorElement | null} */ (el.querySelector('[data-rr-fs-link]'));
      if (link) link.hidden = state === 'done' || !linkTargetExists(link);
    }
    document.dispatchEvent(new CustomEvent(EVENTS.cartTotal, { detail: { total } }));
  },

  async refresh() {
    try {
      const cart = await fetchCart();
      this.render(Number(cart.total_price) || 0);
    } catch {
      /* keep the last known state */
    }
  },

  init() {
    const first = /** @type {HTMLElement | null} */ (document.querySelector('[data-rr-fs]'));
    const total = first ? Number(first.dataset.total) || 0 : 0;
    this.render(this.total ?? total);
  },
};

/** @param {HTMLAnchorElement} link */
function linkTargetExists(link) {
  const href = link.getAttribute('href') || '';
  if (!href.startsWith('#')) return true;
  return href.length > 1 && Boolean(document.getElementById(href.slice(1)));
}

/* ---------- gallery ---------- */

class Gallery {
  /**
   * @param {HTMLElement} root - [data-rr-gallery]
   */
  constructor(root) {
    this.root = root;
    this.track = /** @type {HTMLElement | null} */ (root.querySelector('[data-rr-gal-track]'));
    this.counter = root.querySelector('[data-rr-gal-count]');
    this.nav = /** @type {HTMLElement | null} */ (root.querySelector('[data-rr-gal-nav]'));
    this.lightbox = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-rr-lightbox]'));
    this.lbTrack = /** @type {HTMLElement | null} */ (this.lightbox?.querySelector('[data-rr-lb-track]') ?? null);
    this.stack = root.classList.contains('rr-gal--stack');
    this.index = 0;
    this.lbIndex = 0;
    /** @type {number} */
    this.frame = 0;

    root.addEventListener('click', this.onClick);
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.lightbox) {
      this.lightbox.addEventListener('close', () => unlockScroll(/** @type {HTMLDialogElement} */ (this.lightbox)));
      this.lightbox.addEventListener('keydown', this.onLightboxKey);
      this.lbTrack?.addEventListener('scroll', this.onLightboxScroll, { passive: true });
    }
    this.refresh();
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    if (!this.track) return [];
    return /** @type {HTMLElement[]} */ (Array.from(this.track.children)).filter(
      (el) => el.matches('[data-rr-slide]') && !el.hidden && !el.classList.contains('rr-gal__slide--empty')
    );
  }

  /** @returns {HTMLElement[]} */
  get thumbs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.root.querySelectorAll('[data-rr-thumb]'))).filter(
      (el) => !el.hidden
    );
  }

  /** @returns {HTMLElement[]} */
  get lightboxItems() {
    if (!this.lightbox) return [];
    return /** @type {HTMLElement[]} */ (Array.from(this.lightbox.querySelectorAll('[data-rr-lb-item]'))).filter(
      (el) => !el.hidden
    );
  }

  /**
   * Shows a color's photo first and the photos tagged with that color.
   * @param {string} color
   * @param {DOMStringMap} media - data-rr-* values from the color input
   */
  setColor(color, media) {
    const hero = /** @type {HTMLImageElement | null} */ (this.root.querySelector('[data-rr-hero-img]'));
    if (hero && media.rrSrc) {
      if (media.rrSrcset) hero.srcset = media.rrSrcset;
      hero.src = media.rrSrc;
      if (media.rrAlt) hero.alt = media.rrAlt;
    }
    const caption = /** @type {HTMLElement | null} */ (this.root.querySelector('[data-rr-hero-cap]'));
    if (caption) {
      caption.textContent = color;
      // Colors without their own photo show the main photo: don't label it with the wrong color.
      caption.hidden = media.rrOwn === 'false';
    }
    const heroThumb = /** @type {HTMLImageElement | null} */ (this.root.querySelector('[data-rr-hero-thumb]'));
    if (heroThumb && media.rrThumb) heroThumb.src = media.rrThumb;
    const lightboxHero = /** @type {HTMLImageElement | null} */ (
      this.lightbox?.querySelector('[data-rr-lb-hero] img') ?? null
    );
    if (lightboxHero && media.rrZoomSrc) {
      lightboxHero.src = media.rrZoomSrc;
      if (media.rrAlt) lightboxHero.alt = media.rrAlt;
    }
    for (const el of /** @type {NodeListOf<HTMLElement>} */ (this.root.querySelectorAll('[data-rr-color]'))) {
      el.hidden = el.dataset.rrColor !== color;
    }
    this.refresh();
    if (this.track && this.track.scrollLeft > 2) this.track.scrollTo({ left: 0, behavior: 'auto' });
  }

  refresh() {
    const count = this.slides.length;
    this.root.dataset.count = String(count);
    if (!this.stack) {
      const rest = count - 1;
      const cols = rest <= 1 ? 1 : rest % 3 === 0 ? 3 : 2;
      this.root.style.setProperty('--rr-gal-cols', String(cols));
      this.root.classList.remove('rr-gal--c1', 'rr-gal--c2', 'rr-gal--c3');
      this.root.classList.add(`rr-gal--c${cols}`);
    }
    if (this.nav) this.nav.hidden = count < 2;
    this.updateIndex();
  }

  onScroll = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateIndex());
  };

  updateIndex() {
    const slides = this.slides;
    if (!this.track || !slides.length) return;
    const left = this.track.scrollLeft;
    let index = 0;
    let best = Infinity;
    slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - left);
      if (distance < best) {
        best = distance;
        index = i;
      }
    });
    this.index = index;
    if (this.counter) this.counter.textContent = `${index + 1} / ${slides.length}`;
    const thumbs = this.thumbs;
    if (thumbs.length !== slides.length) return;
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    const current = thumbs[index];
    const strip = current?.parentElement;
    if (current && strip && strip.scrollWidth > strip.clientWidth) {
      const start = current.offsetLeft;
      const end = start + current.offsetWidth;
      if (start < strip.scrollLeft) strip.scrollTo({ left: start - 8, behavior: smoothBehavior() });
      else if (end > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: end - strip.clientWidth + 8, behavior: smoothBehavior() });
      }
    }
  }

  /** @param {number} index */
  goTo(index) {
    const slide = this.slides[index];
    if (!slide || !this.track) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: smoothBehavior() });
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const thumb = /** @type {HTMLElement | null} */ (target.closest('[data-rr-thumb]'));
    if (thumb) {
      this.goTo(this.thumbs.indexOf(thumb));
      return;
    }

    if (!this.lightbox) return;
    if (target.closest('[data-rr-lb-prev]')) return this.lightboxGo(this.lbIndex - 1);
    if (target.closest('[data-rr-lb-next]')) return this.lightboxGo(this.lbIndex + 1);
    if (target.closest('[data-rr-close]') && this.lightbox.contains(target)) {
      this.lightbox.close();
      return;
    }

    const zoom = target.closest('[data-rr-zoom]');
    if (zoom) {
      event.preventDefault();
      this.openLightbox(/** @type {HTMLElement | null} */ (zoom.closest('[data-rr-slide]')));
    }
  };

  /** @param {HTMLElement | null} slide */
  openLightbox(slide) {
    const lightbox = this.lightbox;
    if (!lightbox || lightbox.open) return;
    const photos = this.slides.filter((el) => el.dataset.rrKind === 'photo');
    const index = slide ? Math.max(0, photos.indexOf(slide)) : 0;
    lightbox.showModal();
    lockScroll(lightbox);
    this.lightboxGo(index, false);
  }

  /**
   * @param {number} index
   * @param {boolean} [smooth]
   */
  lightboxGo(index, smooth = true) {
    const items = this.lightboxItems;
    if (!items.length || !this.lbTrack) return;
    const next = Math.max(0, Math.min(items.length - 1, index));
    const item = items[next];
    if (!item) return;
    this.lbTrack.scrollTo({ left: item.offsetLeft, behavior: smooth ? smoothBehavior() : 'auto' });
    this.updateLightbox(next);
  }

  onLightboxScroll = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const items = this.lightboxItems;
      if (!this.lbTrack || !items.length) return;
      const left = this.lbTrack.scrollLeft;
      let index = 0;
      let best = Infinity;
      items.forEach((item, i) => {
        const distance = Math.abs(item.offsetLeft - left);
        if (distance < best) {
          best = distance;
          index = i;
        }
      });
      this.updateLightbox(index);
    });
  };

  /** @param {number} index */
  updateLightbox(index) {
    this.lbIndex = index;
    const total = this.lightboxItems.length;
    const count = this.lightbox?.querySelector('[data-rr-lb-count]');
    if (count) count.textContent = total > 1 ? `${index + 1} / ${total}` : '';
    const prev = /** @type {HTMLButtonElement | null} */ (this.lightbox?.querySelector('[data-rr-lb-prev]') ?? null);
    const next = /** @type {HTMLButtonElement | null} */ (this.lightbox?.querySelector('[data-rr-lb-next]') ?? null);
    if (prev) prev.hidden = total < 2 || index === 0;
    if (next) next.hidden = total < 2 || index >= total - 1;
  }

  /** @param {KeyboardEvent} event */
  onLightboxKey = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.lightboxGo(this.lbIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.lightboxGo(this.lbIndex + 1);
    }
  };
}

/* ---------- size guide ---------- */

class SizeGuide {
  /**
   * @param {HTMLDialogElement} dialog - [data-rr-size-guide]
   * @param {RRProduct} host
   */
  constructor(dialog, host) {
    this.dialog = dialog;
    this.host = host;
    this.table = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-rr-sg-table]'));
    this.message = dialog.querySelector('[data-rr-sg-msg]');
    this.done = dialog.querySelector('[data-rr-sg-done]');
    dialog.addEventListener('click', this.onClick);
    dialog.addEventListener('close', () => unlockScroll(dialog));
  }

  open() {
    if (this.dialog.open) return;
    this.say('');
    this.update();
    this.dialog.showModal();
    lockScroll(this.dialog);
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  /** @param {string} text */
  say(text) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.classList.toggle('is-on', Boolean(text));
  }

  /** Marks the chosen size in the chart and sets the close button label. */
  update() {
    const host = this.host;
    const size = host.sizeChosen ? host.checkedSizeInput()?.value || '' : '';
    for (const row of /** @type {NodeListOf<HTMLElement>} */ (this.dialog.querySelectorAll('[data-rr-row]'))) {
      const on = Boolean(size) && row.dataset.rrRow === size;
      row.classList.toggle('is-on', on);
      row.querySelector('[data-rr-pick-size]')?.setAttribute('aria-pressed', String(on));
    }
    if (this.done) this.done.textContent = size ? fill(host.messages.useSize, { size }) : host.messages.done || '';
    this.renderUnit(host.unit);
  }

  /** @param {string} unit */
  renderUnit(unit) {
    for (const button of /** @type {NodeListOf<HTMLElement>} */ (this.dialog.querySelectorAll('[data-rr-unit]'))) {
      button.setAttribute('aria-pressed', String(button.dataset.rrUnit === unit));
    }
    if (!this.table || this.table.dataset.unit === unit) return;
    this.table.dataset.unit = unit;
    for (const cell of /** @type {NodeListOf<HTMLElement>} */ (this.table.querySelectorAll('td[data-rr-in]'))) {
      cell.textContent = formatMeasure(cell.dataset.rrIn, unit);
    }
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target;
    if (target === this.dialog) {
      this.close();
      return;
    }
    if (!(target instanceof Element)) return;
    if (target.closest('[data-rr-close], [data-rr-sg-done]')) {
      this.close();
      return;
    }
    const unit = /** @type {HTMLElement | null} */ (target.closest('[data-rr-unit]'));
    if (unit) {
      this.host.setUnit(unit.dataset.rrUnit || 'in');
      return;
    }
    const pick = /** @type {HTMLElement | null} */ (target.closest('[data-rr-pick-size]'));
    if (pick) this.host.pickSize(pick.dataset.rrPickSize || '', this);
  };
}

/* ---------- product section ---------- */

class RRProduct extends HTMLElement {
  /** @type {Record<string, string>} */
  messages = {};
  sizeChosen = true;
  requireSize = false;
  unit = 'in';
  /** @type {Gallery | null} */
  gallery = null;
  /** @type {SizeGuide | null} */
  sizeGuide = null;
  /** @type {HTMLElement | null} */
  stickyBar = null;
  lastNeed = 0;

  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.productId = this.dataset.productId || '';
    this.requireSize = this.dataset.requireSize === 'true';
    this.messages = parseJSON(this.querySelector('[data-rr-messages]')) || {};
    this.storageKey = `rr-size:${this.productId}`;

    const table = /** @type {HTMLElement | null} */ (this.querySelector('[data-rr-sg-table]'));
    const savedUnit = storageGet('localStorage', 'rr-unit');
    this.unit = savedUnit === 'cm' || savedUnit === 'in' ? savedUnit : table?.dataset.unit || 'in';

    this.addEventListener('click', this.onClickCapture, { capture: true, signal });
    this.addEventListener('change', this.onChangeCapture, { capture: true, signal });
    this.addEventListener('invalid', this.onInvalid, { capture: true, signal });
    this.addEventListener(EVENTS.productSelect, this.onProductSelect, { signal });

    // A size counts as picked only if this shopper picked it (saved for this tab) and the page
    // was opened on that variant. Shared links and ads never pick a size for anyone.
    let chosen = !this.requireSize;
    if (this.requireSize) {
      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      const saved = storageGet('sessionStorage', this.storageKey);
      chosen = Boolean(urlVariant && saved && urlVariant === saved && this.checkedSizeInput());
    }
    this.setSizeChosen(chosen);
    if (!this.sizeChosen) this.uncheckSizes();
    this.lastSize = this.sizeChosen ? this.checkedSizeInput()?.value || '' : '';

    const galleryRoot = /** @type {HTMLElement | null} */ (this.querySelector('[data-rr-gallery]'));
    this.gallery = galleryRoot ? new Gallery(galleryRoot) : null;

    const guide = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-rr-size-guide]'));
    this.sizeGuide = guide ? new SizeGuide(guide, this) : null;
    this.sizeGuide?.renderUnit(this.unit);

    this.updateSizeLabel();
    this.updateMeasurements();
    this.initStickyBar();
    this.initStickyInfo();
    this.classList.add('is-ready');
    this.emitSelection();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.stickyObserver?.disconnect();
    this.infoObserver?.disconnect();
    if (this.stickyBar) {
      document.documentElement.classList.remove('rr-has-sbar', 'rr-sbar-on');
    }
  }

  /* ----- state ----- */

  /** @returns {HTMLInputElement[]} */
  sizeInputs() {
    return Array.from(this.querySelectorAll('variant-picker input[data-rr-kind="size"]'));
  }

  /** @returns {HTMLInputElement | null} */
  checkedSizeInput() {
    return this.querySelector('variant-picker input[data-rr-kind="size"]:checked');
  }

  currentColor() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.querySelector('variant-picker input[data-rr-kind="color"]:checked')
    );
    if (input) return input.value;
    const single = this.querySelector('variant-picker [data-rr-option="color"] .rr-opt__val');
    return single?.textContent?.trim() || '';
  }

  currentSize() {
    if (!this.sizeChosen) return null;
    const input = this.checkedSizeInput();
    if (input) return input.value;
    const single = this.querySelector('variant-picker [data-rr-option="size"] .rr-opt__val');
    return single?.textContent?.trim() || null;
  }

  get selection() {
    return {
      productId: this.productId,
      color: this.currentColor(),
      size: this.currentSize(),
      chosen: this.sizeChosen,
    };
  }

  /** @param {boolean} chosen */
  setSizeChosen(chosen) {
    this.sizeChosen = chosen || !this.requireSize;
    this.dataset.sizeChosen = String(this.sizeChosen);
    this.toggleGate(!this.sizeChosen);
    if (!this.sizeChosen) storageSet('sessionStorage', this.storageKey, null);
  }

  /**
   * While no size is picked, an empty required field keeps the product form from submitting, so
   * Horizon's add to cart (and the browser) stop before anything is added.
   * @param {boolean} on
   */
  toggleGate(on) {
    for (const form of this.querySelectorAll('product-form-component form')) {
      const gate = form.querySelector('[data-rr-gate]');
      if (on && !gate) {
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'rr-gate';
        input.required = true;
        input.tabIndex = -1;
        input.autocomplete = 'off';
        input.setAttribute('aria-hidden', 'true');
        input.dataset.rrGate = '';
        form.append(input);
      } else if (!on && gate) {
        gate.remove();
      }
    }
  }

  uncheckSizes() {
    for (const input of this.sizeInputs()) input.checked = false;
  }

  emitSelection() {
    document.dispatchEvent(new CustomEvent(EVENTS.selection, { detail: this.selection }));
  }

  /* ----- messages ----- */

  /** @param {string} text */
  say(text) {
    const el = this.querySelector('[data-rr-size-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('is-on', Boolean(text));
  }

  /**
   * Explains why a size can't be picked in the selected color.
   * @param {HTMLInputElement} input
   */
  explain(input) {
    const m = this.messages;
    const color = this.currentColor();
    const size = input.value;
    const available = splitList(input.dataset.rrColors).filter((name) => name !== color);
    const made = splitList(input.dataset.rrMade);
    if (color && made.includes(color)) return fill(m.soldOut, { size, color });
    if (!color) return fill(m.soldOut, { size, color: '' }).replace(/\s+in\s*\.$/, '.');
    const first = fill(m.notMade, { size, color });
    return available.length ? `${first} ${fill(m.comesIn, { colors: joinList(available, m.and) })}` : first;
  }

  /**
   * Asks for a size: message, shake, and (from the sticky bar or bundle) scroll to the sizes.
   * @param {{ scroll?: boolean }} [options]
   */
  needSize({ scroll = false } = {}) {
    const now = performance.now();
    if (now - this.lastNeed < 150) return;
    this.lastNeed = now;
    this.say(this.messages.pick || '');
    const fieldset = /** @type {HTMLElement | null} */ (this.querySelector('variant-picker .rr-opt--size'));
    if (!fieldset) return;
    fieldset.classList.remove('is-err');
    void fieldset.offsetWidth;
    fieldset.classList.add('is-err');
    fieldset.addEventListener('animationend', () => fieldset.classList.remove('is-err'), { once: true });
    if (scroll) fieldset.scrollIntoView({ behavior: smoothBehavior(), block: 'center' });
    const first = /** @type {HTMLInputElement | null} */ (
      fieldset.querySelector('input:not([aria-disabled="true"])')
    );
    first?.focus({ preventScroll: true });
  }

  /* ----- events ----- */

  /**
   * Clicking a size that isn't made (or is sold out) in this color explains why instead of
   * selecting it.
   * @param {MouseEvent} event
   */
  onClickCapture = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.rrKind !== 'size') return;
    if (input.getAttribute('aria-disabled') !== 'true') return;
    event.preventDefault();
    this.say(this.explain(input));
  };

  /**
   * Runs before Horizon's own change handler (capture phase), so the request Horizon builds
   * already has the right size state.
   * @param {Event} event
   */
  onChangeCapture = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('variant-picker')) return;
    const kind = input.dataset.rrKind;

    if (kind === 'size') {
      if (input.getAttribute('aria-disabled') === 'true') {
        // Reached with the keyboard: keep the previous size and explain.
        event.stopImmediatePropagation();
        input.checked = false;
        const previous = this.sizeChosen ? this.sizeInputs().find((el) => el.value === this.lastSize) : null;
        if (previous) previous.checked = true;
        this.say(this.explain(input));
        return;
      }
      this.setSizeChosen(true);
      this.lastSize = input.value;
      this.say('');
    } else if (kind === 'color') {
      const color = input.value;
      this.applyColor(input);
      const size = this.sizeChosen && this.requireSize ? this.checkedSizeInput() : null;
      if (size && !splitList(size.dataset.rrColors).includes(color)) {
        const made = splitList(size.dataset.rrMade).includes(color);
        size.checked = false;
        this.setSizeChosen(false);
        this.lastSize = '';
        this.say(fill(made ? this.messages.soldOut : this.messages.switched, { size: size.value, color }));
      } else if (this.sizeChosen) {
        this.say('');
      }
      this.previewSizeAvailability(color);
    }

    this.updateSizeLabel();
    this.updateMeasurements();
    this.sizeGuide?.update();
    this.updateStickyBar();
  };

  /**
   * After Horizon re-renders the picker for the new selection.
   * @param {Event & { promise?: Promise<any> }} event
   */
  onProductSelect = (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    if (!picker || !this.contains(picker)) return;
    event.promise
      ?.then((result) => {
        const resource = result?.detail?.resource ?? null;
        if (this.requireSize && this.sizeChosen && !resource) {
          const size = this.checkedSizeInput();
          this.setSizeChosen(false);
          this.lastSize = '';
          if (size) this.say(this.explain(size));
        }
        if (!this.sizeChosen) {
          this.uncheckSizes();
        } else if (this.requireSize && resource?.id) {
          storageSet('sessionStorage', this.storageKey, String(resource.id));
        }
        this.updateSizeLabel();
        this.updateMeasurements();
        this.sizeGuide?.update();
        // Horizon swaps the price after this handler runs.
        requestAnimationFrame(() => this.updateStickyBar());
        this.emitSelection();
      })
      .catch(() => {
        /* aborted by a newer selection */
      });
  };

  /**
   * The product form is invalid while no size is picked: ask for one instead of the browser's
   * own bubble on a hidden field.
   * @param {Event} event
   */
  onInvalid = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches('[data-rr-gate]')) return;
    event.preventDefault();
    this.needSize();
  };

  /* ----- color, sizes, measurements ----- */

  /** @param {HTMLInputElement} input - the picked color */
  applyColor(input) {
    const color = input.value;
    const label = this.querySelector('[data-rr-color-val]');
    if (label) label.textContent = color;
    this.gallery?.setColor(color, input.dataset);
    const stickyImage = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-rr-sbar-img]'));
    if (stickyImage && input.dataset.rrThumb) stickyImage.src = input.dataset.rrThumb;
  }

  /**
   * Shows which sizes exist in the new color right away; Horizon's re-render confirms it.
   * @param {string} color
   */
  previewSizeAvailability(color) {
    const m = this.messages;
    for (const input of this.sizeInputs()) {
      const available = splitList(input.dataset.rrColors).includes(color);
      const made = splitList(input.dataset.rrMade).includes(color);
      input.closest('label')?.classList.toggle('is-na', !available);
      if (available) input.removeAttribute('aria-disabled');
      else input.setAttribute('aria-disabled', 'true');
      const base = input.dataset.rrUp ? `${input.value}, +${input.dataset.rrUp}` : input.value;
      const state = available ? '' : made ? m.stateSoldOut : fill(m.stateNotMade, { color });
      input.setAttribute('aria-label', state ? `${base}, ${state}` : base);
    }
  }

  updateSizeLabel() {
    const label = this.querySelector('[data-rr-size-val]');
    if (!label) return;
    const input = this.sizeChosen ? this.checkedSizeInput() : null;
    label.textContent = input ? input.value : '';
  }

  updateMeasurements() {
    const line = /** @type {HTMLElement | null} */ (this.querySelector('[data-rr-meas]'));
    if (!line) return;
    const input = this.sizeChosen ? this.checkedSizeInput() : null;
    if (!input) {
      line.hidden = true;
      line.textContent = '';
      return;
    }
    const { rrChest: chest, rrLength: length } = input.dataset;
    line.textContent = chest
      ? fill(this.messages.measure, {
          size: input.value,
          chest: formatMeasure(chest, this.unit, true),
          length: formatMeasure(length, this.unit, true),
        })
      : fill(this.messages.measureMissing, { size: input.value });
    line.hidden = !line.textContent;
  }

  /** @param {string} unit */
  setUnit(unit) {
    this.unit = unit === 'cm' ? 'cm' : 'in';
    storageSet('localStorage', 'rr-unit', this.unit);
    this.sizeGuide?.renderUnit(this.unit);
    this.updateMeasurements();
  }

  /**
   * Picks a size from the size guide chart.
   * @param {string} size
   * @param {SizeGuide} guide
   */
  pickSize(size, guide) {
    const input = this.sizeInputs().find((el) => el.value === size);
    if (!input) return;
    if (input.getAttribute('aria-disabled') === 'true') {
      guide.say(this.explain(input));
      return;
    }
    guide.say('');
    if (!input.checked || !this.sizeChosen) input.click();
    guide.update();
  }

  /* ----- sticky buy bar ----- */

  initStickyBar() {
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-rr-sbar]'));
    const button = this.querySelector('[data-rr-atc]');
    if (!bar) return;
    if (!button) {
      bar.remove();
      return;
    }
    this.stickyBar = bar;
    const addLabel = bar.querySelector('.rr-sbar__add');
    if (addLabel) addLabel.setAttribute('data-label', addLabel.textContent || '');
    document.documentElement.classList.add('rr-has-sbar');

    const mode = this.dataset.sticky;
    const small = window.matchMedia('(max-width: 989px)');
    let inView = true;
    let passed = false;
    const update = () => {
      const show = !inView && (passed || (mode === 'mobile_load' && small.matches));
      bar.classList.toggle('is-on', show);
      bar.toggleAttribute('inert', !show);
      document.documentElement.classList.toggle('rr-sbar-on', show);
    };
    this.stickyObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      inView = entry.isIntersecting;
      passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    });
    this.stickyObserver.observe(button);
    small.addEventListener('change', update, { signal: this.abort?.signal });
    bar.querySelector('[data-rr-sbar-btn]')?.addEventListener('click', this.onStickyClick, {
      signal: this.abort?.signal,
    });
    this.updateStickyBar();
  }

  onStickyClick = () => {
    if (!this.sizeChosen) {
      this.needSize({ scroll: true });
      return;
    }
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-rr-atc]'));
    if (!button || button.disabled) return;
    // The fly-to-cart animation starts from the page button, which is off screen here.
    const container = /** @type {HTMLElement | null} */ (button.closest('add-to-cart-component'));
    const animation = container?.dataset.addToCartAnimation;
    if (container) container.dataset.addToCartAnimation = 'false';
    button.click();
    if (container && animation !== undefined) container.dataset.addToCartAnimation = animation;
  };

  priceText() {
    const price = this.querySelector('[data-rr-price]');
    if (!price) return '';
    const copy = /** @type {HTMLElement} */ (price.cloneNode(true));
    copy.querySelectorAll('.rr-sr, .visually-hidden').forEach((el) => el.remove());
    return copy.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  updateStickyBar() {
    const bar = this.stickyBar;
    if (!bar) return;
    const variant = bar.querySelector('[data-rr-sbar-variant]');
    const size = this.sizeChosen ? this.checkedSizeInput()?.value || '' : '';
    const options = [this.currentColor(), size].filter(Boolean).join(' / ');
    if (variant) variant.textContent = [options, this.priceText()].filter(Boolean).join(' · ');

    const pageButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-rr-atc]'));
    const stickyButton = /** @type {HTMLButtonElement | null} */ (bar.querySelector('[data-rr-sbar-btn]'));
    const addLabel = bar.querySelector('.rr-sbar__add');
    if (!stickyButton || !pageButton) return;
    const unavailable = pageButton.disabled;
    stickyButton.disabled = unavailable;
    if (addLabel) {
      const pageLabel = pageButton.querySelector('.rr-atc__add')?.textContent?.trim();
      addLabel.textContent = unavailable && pageLabel ? pageLabel : addLabel.getAttribute('data-label') || '';
    }
  }

  /* ----- sticky product info (desktop) ----- */

  initStickyInfo() {
    if (this.dataset.stickyInfo !== 'true') return;
    const info = /** @type {HTMLElement | null} */ (this.querySelector('[data-rr-info]'));
    if (!info || !('ResizeObserver' in window)) return;
    this.infoObserver = new ResizeObserver(() => {
      this.style.setProperty('--rr-info-h', `${info.offsetHeight}px`);
    });
    this.infoObserver.observe(info);
  }
}

/* ---------- "Make it three" ---------- */

class RRBundle extends HTMLElement {
  /** @type {Array<any>} */
  items = [];
  /** @type {Array<{ id: number, quantity: number }>} */
  lines = [];
  busy = false;
  cartTotal = 0;

  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.config = parseJSON(this.querySelector('[data-rr-bun-config]')) || {};
    this.openCart = this.config.openCart !== false;
    this.cartTotal = Number(this.config.cartTotal) || 0;
    this.items = Array.from(this.querySelectorAll('[data-rr-bi]')).map((el) => {
      const data = parseJSON(el.querySelector('[data-rr-bi-data]')) || { variants: [] };
      return {
        el,
        data,
        color: data.color || '',
        include: /** @type {HTMLInputElement | null} */ (el.querySelector('[data-rr-bi-on]')),
        select: /** @type {HTMLSelectElement | null} */ (el.querySelector('[data-rr-bi-color]')),
        image: /** @type {HTMLImageElement | null} */ (el.querySelector('[data-rr-bi-img]')),
        price: el.querySelector('[data-rr-bi-price]'),
        message: el.querySelector('[data-rr-bi-msg]'),
        colorText: el.querySelector('[data-rr-bi-color-text]'),
      };
    });

    const host = /** @type {RRProduct | null} */ (
      document.querySelector(`rr-product[data-product-id="${this.dataset.productId}"]`)
    );
    this.selection = host && typeof host.currentColor === 'function' ? host.selection : null;

    document.addEventListener(EVENTS.selection, this.onSelection, { signal });
    document.addEventListener(
      EVENTS.cartTotal,
      (event) => {
        this.cartTotal = Number(/** @type {CustomEvent} */ (event).detail?.total) || 0;
        this.render();
      },
      { signal }
    );
    this.addEventListener('change', this.onChange, { signal });
    this.addEventListener('click', this.onClick, { signal });
    this.render();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /** @param {Event} event */
  onSelection = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || String(detail.productId) !== String(this.dataset.productId)) return;
    this.selection = detail;
    this.render();
  };

  /** @param {Event} event */
  onChange = (event) => {
    const target = event.target;
    const item = this.items.find((entry) => entry.el.contains(target));
    if (!item) return;
    if (target === item.select && item.select) item.color = item.select.value;
    this.render();
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-rr-bun-pick]')) {
      this.askForSize();
    } else if (target.closest('[data-rr-bun-add]')) {
      this.add();
    }
  };

  askForSize() {
    document.dispatchEvent(new CustomEvent(EVENTS.needSize, { detail: { productId: this.dataset.productId } }));
  }

  /**
   * @param {any} item
   * @param {string} color
   */
  variantsInColor(item, color) {
    const { colorPos = 0, variants = [] } = item.data;
    return variants.filter(
      (/** @type {any} */ variant) => !colorPos || !color || variant.o[colorPos - 1] === color
    );
  }

  render() {
    const c = this.config || {};
    const selection = this.selection;
    const needsSize = this.items.some((item) => item.data.sizePos > 0);
    const size = selection?.chosen ? selection.size : null;
    const ready = !needsSize || Boolean(size);
    let count = 0;
    let subtotal = 0;
    let varies = false;
    /** @type {Array<{ id: number, quantity: number }>} */
    const lines = [];

    for (const item of this.items) {
      const { data } = item;
      if (data.main && selection?.color) item.color = selection.color;
      const color = item.color;

      const src = data.images?.[color];
      if (item.image && src && item.image.getAttribute('src') !== src) {
        item.image.removeAttribute('srcset');
        item.image.removeAttribute('sizes');
        item.image.src = src;
      }
      if (data.main && item.colorText) item.colorText.textContent = color;

      const included = data.main || !item.include || item.include.checked;
      const inColor = this.variantsInColor(item, color);
      let variant = null;
      let message = '';
      if (ready && needsSize) {
        variant =
          inColor.find(
            (/** @type {any} */ v) => (!data.sizePos || v.o[data.sizePos - 1] === size) && v.a
          ) || null;
        if (!variant) message = fill(c.notMade, { size: size || '', color });
      } else if (ready) {
        variant = inColor.find((/** @type {any} */ v) => v.a) || null;
      }

      let cents;
      let from = false;
      if (variant) {
        cents = variant.p;
      } else {
        const available = inColor.filter((/** @type {any} */ v) => v.a);
        const prices = (available.length ? available : inColor).map((/** @type {any} */ v) => Number(v.p));
        cents = prices.length ? Math.min(...prices) : 0;
        from = new Set(prices).size > 1;
      }
      if (item.price) item.price.textContent = `${from && !message ? `${c.from} ` : ''}${money(cents)}`;
      if (item.message) item.message.textContent = message;
      item.el.classList.toggle('is-na', Boolean(message));
      item.el.classList.toggle('is-off', !included || Boolean(message));

      if (included && !message) {
        count += 1;
        subtotal += cents;
        if (from) varies = true;
        if (variant) lines.push({ id: variant.id, quantity: 1 });
      }
    }
    this.lines = lines;

    const set = (/** @type {string} */ selector, /** @type {string} */ text) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = text;
    };
    set('[data-rr-bun-size]', size || c.sizeNone || '');
    set('[data-rr-bun-pick]', size ? c.change || '' : c.pick || '');
    set('[data-rr-bun-count]', fill(count === 1 ? c.countOne : c.countOther, { count }));
    const total = `${varies && !ready ? `${c.from} ` : ''}${money(subtotal)}`;
    set('[data-rr-bun-sub]', total);
    set('[data-rr-bun-tot]', total);
    const ship = this.querySelector('[data-rr-bun-ship]');
    const free = Number(c.threshold) > 0 && subtotal + (this.cartTotal || 0) >= Number(c.threshold);
    if (ship) {
      ship.textContent = free ? c.free || '' : c.calc || '';
      ship.classList.toggle('rr-ok', free);
    }

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-rr-bun-add]'));
    if (!button || this.busy) return;
    if (!ready) {
      button.textContent = c.btnPick || '';
      button.disabled = false;
      button.dataset.mode = 'pick';
    } else if (!lines.length) {
      button.textContent = c.btnNone || '';
      button.disabled = true;
      button.dataset.mode = 'none';
    } else {
      const template = lines.length === this.items.length ? c.btnAll : c.btnSome;
      button.textContent = fill(template, { count: lines.length, price: money(subtotal) });
      button.disabled = false;
      button.dataset.mode = 'add';
    }
  }

  async add() {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-rr-bun-add]'));
    if (!button || this.busy) return;
    if (button.dataset.mode === 'pick') {
      this.askForSize();
      return;
    }
    if (!this.lines.length) return;

    const c = this.config || {};
    const note = this.querySelector('[data-rr-bun-note]');
    const items = this.lines.map((line) => ({ id: line.id, quantity: 1 }));
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    this.busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = c.adding || '';
    note?.classList.remove('is-err');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(sectionIds.length ? { items, sections: sectionIds.join(',') } : { items }),
      });
      const data = await response.json().catch(() => ({}));
      /** @type {any} */
      let cart = null;
      try {
        cart = await fetchCart();
      } catch {
        cart = null;
      }

      if (!response.ok || data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
          detail: {
            didError: true,
            items: cart?.items ?? [],
            source: 'rr-bundle',
            sourceId: this.id || 'rr-bundle',
            itemCount: 0,
            productId: this.dataset.productId,
          },
        });
        this.fail(data.description || data.message);
      } else {
        deferred.resolve({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
          detail: {
            didError: false,
            items: cart?.items ?? data.items ?? [],
            source: 'rr-bundle',
            sourceId: this.id || 'rr-bundle',
            itemCount: items.length,
            productId: this.dataset.productId,
            sections: data.sections,
          },
        });
        if (note) note.textContent = c.note || '';
      }
    } catch (error) {
      deferred.reject(error);
      this.fail();
    } finally {
      this.busy = false;
      button.removeAttribute('aria-busy');
      this.render();
    }
  }

  /** @param {string} [message] */
  fail(message) {
    const note = this.querySelector('[data-rr-bun-note]');
    if (!note) return;
    note.textContent = message || this.config?.error || '';
    note.classList.add('is-err');
  }
}

/* ---------- reviews ---------- */

class RRReviews extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-rr-write-review]') : null;
    if (!button) return;
    const app = /** @type {HTMLElement | null} */ (this.querySelector('[data-rr-review-app]'));
    if (app) app.hidden = false;
    this.openForm(0);
  };

  /** @param {number} attempt */
  openForm(attempt) {
    const link = /** @type {HTMLElement | null} */ (
      this.querySelector('.jdgm-write-rev-link, [class*="write-rev-link"], [class*="write-review"] a, a[href*="#write-review"]')
    );
    if (link) {
      link.click();
      const form = this.querySelector('.jdgm-form-wrapper, .jdgm-form, form');
      requestAnimationFrame(() => (form || link).scrollIntoView({ behavior: smoothBehavior(), block: 'start' }));
      return;
    }
    if (attempt < 1) {
      setTimeout(() => this.openForm(attempt + 1), 1200);
      return;
    }
    const note = /** @type {HTMLElement | null} */ (this.querySelector('[data-rr-review-note]'));
    if (note) note.hidden = false;
  }
}

/* ---------- page-wide wiring ---------- */

if (!customElements.get('rr-product')) customElements.define('rr-product', RRProduct);
if (!customElements.get('rr-bundle')) customElements.define('rr-bundle', RRBundle);
if (!customElements.get('rr-reviews')) customElements.define('rr-reviews', RRReviews);

/** Turns [size_guide] buttons into plain text when the page has no size guide. */
function downgradeGuideButtons() {
  if (document.querySelector('[data-rr-size-guide]')) return;
  for (const button of document.querySelectorAll('.rr-inline-btn[data-rr-open-size-guide]')) {
    const span = document.createElement('span');
    span.textContent = button.textContent;
    button.replaceWith(span);
  }
}

if (!window.__rrPdpWired) {
  window.__rrPdpWired = true;

  // Size guide openers anywhere on the page (picker link, fit card, FAQ answers, #size-guide links).
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const opener = target?.closest('[data-rr-open-size-guide], a[href="#size-guide"]');
    if (!opener || opener.closest('.quick-add-modal, quick-add-dialog, #quick-add-modal-content')) return;
    const host = /** @type {RRProduct | null} */ (opener.closest('rr-product') || document.querySelector('rr-product'));
    if (!host?.sizeGuide) return;
    event.preventDefault();
    host.sizeGuide.open();
  });

  // The bundle (or anything else) asking for a size.
  document.addEventListener(EVENTS.needSize, (event) => {
    const productId = /** @type {CustomEvent} */ (event).detail?.productId;
    const host = /** @type {RRProduct | null} */ (
      (productId && document.querySelector(`rr-product[data-product-id="${productId}"]`)) ||
        document.querySelector('rr-product')
    );
    host?.needSize?.({ scroll: true });
  });

  // Cart changes: free-shipping progress, and opening the cart after adding from this page.
  document.addEventListener(EVENTS.cartLinesUpdate, (event) => {
    const update = /** @type {any} */ (event);
    const promise = update.promise;
    promise?.then(() => freeShipping.refresh()).catch(() => {});

    const target = event.target instanceof Element ? event.target : null;
    const host = /** @type {any} */ (target?.closest('rr-product, rr-bundle'));
    if (!host || update.action !== 'add') return;
    const wantsOpen = host.localName === 'rr-product' ? host.dataset.openCart === 'true' : host.openCart;
    if (!wantsOpen) return;
    promise
      ?.then((/** @type {any} */ result) => {
        if (result?.detail?.didError) return;
        requestAnimationFrame(openCartDrawer);
      })
      .catch(() => {});
  });

  document.addEventListener(EVENTS.cartDiscountUpdate, (event) => {
    /** @type {any} */ (event).promise?.then(() => freeShipping.refresh()).catch(() => {});
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) freeShipping.refresh();
  });

  // Theme editor: open the accordion row or question being edited, and re-run page helpers.
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const details = target?.closest('details');
    if (details) details.open = true;
  });
  document.addEventListener('shopify:section:load', () => {
    renderDeliveryDates();
    freeShipping.init();
    downgradeGuideButtons();
  });
}

renderDeliveryDates();
freeShipping.init();
downgradeGuideButtons();
